import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./lib/auth";
import { ImagesProvider } from "./lib/images";
import PublicLayout from "./components/PublicLayout";
import AdminLayout from "./components/AdminLayout";

import Home from "./pages/Home";
import Courses from "./pages/Courses";
import About from "./pages/About";
import Schedule from "./pages/Schedule";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Gallery from "./pages/Gallery";
import Contact from "./pages/Contact";
import PaymentSuccess from "./pages/PaymentSuccess";
import Policy from "./pages/Policy";
import Unsubscribe from "./pages/Unsubscribe";

import AdminLogin from "./pages/admin/Login";
import Dashboard from "./pages/admin/Dashboard";
import Leads from "./pages/admin/Leads";
import Students from "./pages/admin/Students";
import Invoices from "./pages/admin/Invoices";
import WelcomePack from "./pages/admin/WelcomePack";
import BlogAdmin from "./pages/admin/BlogAdmin";
import Social from "./pages/admin/Social";
import Settings from "./pages/admin/Settings";
import SiteImages from "./pages/admin/SiteImages";
import Team from "./pages/admin/Team";
import Curriculum from "./pages/admin/Curriculum";
import BulkEmail from "./pages/admin/BulkEmail";
import Analytics from "./pages/admin/Analytics";
import Redirects from "./pages/admin/Redirects";
import AdminUsers from "./pages/admin/AdminUsers";
import SetPassword from "./pages/admin/SetPassword";
import Vouchers from "./pages/admin/Vouchers";
import Security from "./pages/admin/Security";
import LegacyRedirect from "./components/LegacyRedirect";
import Enrol from "./pages/Enrol";

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen admin-bg flex items-center justify-center">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <ImagesProvider>
          <BrowserRouter>
          <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/courses" element={<Courses />} />
            <Route path="/classes" element={<Navigate to="/courses" replace />} />
            <Route path="/about" element={<About />} />
            <Route path="/schedule" element={<Schedule />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/terms" element={<Policy slug="terms" />} />
            <Route path="/privacy" element={<Policy slug="privacy" />} />
            <Route path="/cookies" element={<Policy slug="cookies" />} />
            <Route path="/accessibility" element={<Policy slug="accessibility" />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
          </Route>

          <Route path="/enrol/:token" element={<Enrol />} />

          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/set-password" element={<SetPassword />} />
          <Route path="/admin" element={<RequireAuth><AdminLayout /></RequireAuth>}>
            <Route index element={<Dashboard />} />
            <Route path="leads" element={<Leads />} />
            <Route path="students" element={<Students />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="vouchers" element={<Vouchers />} />
            <Route path="security" element={<Security />} />
            <Route path="welcome" element={<WelcomePack />} />
            <Route path="blog" element={<BlogAdmin />} />
            <Route path="social" element={<Social />} />
            <Route path="images" element={<SiteImages />} />
            <Route path="team" element={<Team />} />
            <Route path="curriculum" element={<Curriculum />} />
            <Route path="bulk-email" element={<BulkEmail />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="redirects" element={<Redirects />} />
            <Route path="settings" element={<Settings />} />
            <Route path="users" element={<AdminUsers />} />
          </Route>

          <Route path="*" element={<LegacyRedirect fallback={<Navigate to="/" replace />} />} />
        </Routes>
        </BrowserRouter>
        </ImagesProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}
